/**
 * Cell-renderer components for the Students EntityTable.
 *
 * Extracted from studentsEntityConfig.tsx so that file's only exports are
 * config objects/functions (react-refresh/only-export-components — Fast
 * Refresh needs a file to export either only components or only
 * non-components, not a mix).
 */

import React from 'react';
import { Avatar } from '@/components/common/Avatar';
import { Badge } from '@/components/common/Badge';
import type { StudentResponse } from '@features/people/api/students';
import { getFullName } from '@/utils';

export const StudentCell: React.FC<{ student: StudentResponse }> = ({ student }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <Avatar
      firstName={student.first_name}
      lastName={student.last_name}
      src={student.photo_url ?? undefined}
      size="sm"
    />
    <div>
      <div style={{ fontWeight: 500, lineHeight: 1.3 }}>
        {getFullName(student.first_name, student.last_name)}
      </div>
      <div style={{ fontSize: 12, color: '#64748b' }}>{student.email ?? 'No email'}</div>
    </div>
  </div>
);

export const TagsCell: React.FC<{ student: StudentResponse }> = ({ student }) => {
  const tags = student.tags ?? [];
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {tags.slice(0, 2).map((t) => (
        <Badge key={t} variant="primary" size="sm">
          {t}
        </Badge>
      ))}
      {tags.length > 2 && (
        <Badge variant="default" size="sm">
          +{tags.length - 2}
        </Badge>
      )}
    </div>
  );
};

type PaymentStatus = 'paid' | 'outstanding' | 'none';

const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: '✓ Paid',
  outstanding: 'Outstanding',
  none: '—',
};

const PAYMENT_STATUS_COLOR: Record<PaymentStatus, string> = {
  paid: '#16a34a',
  outstanding: '#dc2626',
  none: '#94a3b8',
};

export const PaymentStatusCell: React.FC<{ student: StudentResponse }> = ({ student }) => {
  const status = (student.billing_summary?.payment_status ?? 'none') as PaymentStatus;
  return (
    <span style={{ color: PAYMENT_STATUS_COLOR[status], fontWeight: 500, fontSize: 13 }}>
      {PAYMENT_STATUS_LABEL[status]}
    </span>
  );
};

export const RemainingClassesCell: React.FC<{ student: StudentResponse }> = ({ student }) => {
  const remaining = student.billing_summary?.remaining_classes;
  if (remaining === null || remaining === undefined) {
    return <span style={{ color: '#94a3b8' }}>—</span>;
  }
  const color = remaining === 0 ? '#dc2626' : remaining <= 3 ? '#d97706' : '#16a34a';
  return <span style={{ color, fontWeight: 600 }}>{remaining}</span>;
};
